/* iter55u — Extracted from AdminHelpDrawer.jsx.
 *
 * `HelpSectionList` is the searchable left rail — pure presentational.
 * `HelpSectionView` is the read-only right-pane renderer with an "Edit"
 * button that fires the drawer's `onBeginEdit` prop.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Pencil, ChevronRight, AlertCircle } from 'lucide-react';
import { Block } from './AdminHelpBlocks';

function highlight(text, query) {
  if (!text || !query.trim()) return text;
  const escaped = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const split = new RegExp(`(${escaped})`, 'i');
  const test  = new RegExp(escaped, 'i');
  const parts = String(text).split(split);
  return parts.map((p, i) =>
    test.test(p)
      ? <mark key={`m-${i}-${p}`} className="rounded-sm bg-yellow-200 px-0.5">{p}</mark>
      : <React.Fragment key={`f-${i}-${p}`}>{p}</React.Fragment>,
  );
}

export function HelpSectionList({ visible, loading, activeId, onSelect, query, listRef }) {
  return (
    <div ref={listRef} className="w-56 shrink-0 overflow-y-auto border-r border-border" data-testid="admin-help-list">
      {visible.map((s) => {
        const key = `${s.env}:${s.panel_id}`;
        const isActive = key === activeId;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onSelect(key)}
            data-testid={`admin-help-row-${s.panel_id}`}
            className={
              'flex w-full items-center justify-between gap-2 border-b border-border/50 px-3 py-2 text-left text-[12px] hover:bg-secondary ' +
              (isActive ? 'bg-primary/10 font-semibold text-primary' : '')
            }
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate">{highlight(s.title, query)}</span>
              {s.summary && (
                <span className="mt-0.5 block truncate text-[10.5px] font-normal text-muted-foreground">
                  {highlight(s.summary, query)}
                </span>
              )}
            </span>
            <ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
          </button>
        );
      })}
      {!loading && visible.length === 0 && (
        <div className="flex items-center gap-1.5 px-3 py-4 text-[11.5px] text-muted-foreground">
          <AlertCircle className="h-3.5 w-3.5" /> No matches.
        </div>
      )}
    </div>
  );
}

export function HelpSectionView({ active, onBeginEdit }) {
  return (
    <div className="space-y-3" data-testid={`admin-help-content-${active.panel_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold leading-tight">{active.title}</h3>
          {active.summary && <p className="mt-0.5 text-[11.5px] text-muted-foreground">{active.summary}</p>}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onBeginEdit}
          className="h-7 gap-1.5 text-[11px]"
          data-testid="admin-help-edit"
        >
          <Pencil className="h-3 w-3" /> Edit
        </Button>
      </div>
      <div className="space-y-3">
        {(active.blocks || []).map((b, i) => (
          <Block
            key={`${i}-${b.kind}-${String(b.text || (b.items && b.items[0]) || '').slice(0, 24)}`}
            b={b}
          />
        ))}
        {(!active.blocks || active.blocks.length === 0) && (
          <p className="text-[12px] italic text-muted-foreground">No content yet — click Edit to add some.</p>
        )}
      </div>
    </div>
  );
}
